import React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import EnterpriseHomeApp from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';
import { normalizeSearchString, searchQueryWithoutSource } from 'bundles/unified-common/utils/orgSearchQuery';

const PROGRAMS_ALL_PREFIX = '/programs/all/';

/** When present in the URL, skip redirect to /organizations so the legacy /programs/all page can be reached (e.g. preview/QA). */
const USE_LEGACY_PROGRAMS_ALL_PARAM = 'useLegacyProgramsAll';

function isLegacyProgramsAllRouteCheck(search: string): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(search).get(USE_LEGACY_PROGRAMS_ALL_PARAM) !== null;
}

/**
 * Route handler for programs/all. When orgScopedHome is on, redirects to /organizations/:orgSlug/...
 * unless the legacy-route param is in the query (so preview/QA can still reach the legacy URL).
 * Consumer and org search use path /search; program search uses /browse for historical reasons.
 * We map /browse → /search and drop the source param. When the flag is off, renders children.
 */
function buildProgramsAllRedirectUrl(pathname: string, search: string): string | null {
  if (!pathname.startsWith(PROGRAMS_ALL_PREFIX)) return null;
  const afterPrefix = pathname.slice(PROGRAMS_ALL_PREFIX.length);
  const [orgSlug, ...rest] = afterPrefix.split('/');
  const subpath = rest.length > 0 ? `/${rest.join('/')}` : '';
  const rawSearch = search || '';
  const isSearch = subpath === '/browse' || subpath === '/browse/';
  const orgPath = isSearch ? `/organizations/${orgSlug}/search` : `/organizations/${orgSlug}${subpath}`;
  const searchPart = isSearch ? searchQueryWithoutSource(rawSearch) : normalizeSearchString(rawSearch);
  return searchPart ? `${orgPath}${searchPart}` : orgPath;
}

type ProgramsAllRouteHandlerProps = {
  children?: React.ReactNode;
  params?: { orgSlug: string };
  [key: string]: unknown;
};

const ProgramsAllRouteHandler = (props: ProgramsAllRouteHandlerProps) => {
  const isOrgScopedHome = GrowthDiscoveryEpicClient.preview('orgScopedHome');
  const { pathname, search } = useLocation();
  const isLegacyProgramsAllRoute = isLegacyProgramsAllRouteCheck(search || '');

  const shouldRedirect = isOrgScopedHome && !isLegacyProgramsAllRoute && pathname.startsWith(PROGRAMS_ALL_PREFIX);
  const redirectUrl = shouldRedirect ? buildProgramsAllRedirectUrl(pathname, search || '') : null;

  if (redirectUrl) {
    redirectClientOrServer(redirectUrl);
    return null;
  }

  return <EnterpriseHomeApp {...props}>{props.children}</EnterpriseHomeApp>;
};

export default ProgramsAllRouteHandler;
